/**
 * What a timesheet is going to pay — the estimate shown while it is being
 * edited.
 *
 * This MIRRORS resolveEntryPay() and computeFigures() in
 * server/utils/paystubCalc.js, which is what actually writes the paystub. The
 * two answering the same question differently is not a cosmetic problem: it is
 * exactly what made #202 silent. A $500 fixed-price job was grossed as
 * 8 x $500 = $4000, and the "Est. Pay" line agreed with neither the truth nor
 * the paystub, so nothing on screen ever contradicted anything.
 *
 * So the precedence lives in ONE function here, mirroring one function there,
 * and timesheetPay.test.js runs both over the same matrix and fails if they
 * ever disagree. Duplicated arithmetic is fine; duplicated *rules* are not.
 */

/**
 * Fold a job onto a form entry so it has the shape the server resolver reads.
 * The form keeps the job separately (and uses the string 'custom' as a
 * sentinel for "no job at all"), the server joins it onto the row.
 */
export function withJob(entry, job) {
  return {
    ...entry,
    job_pay_type: job ? job.pay_type : null,
    job_pay_amount: job ? job.pay_amount : null,
  };
}

/**
 * Which rate applies to one entry, and of what kind.
 *
 * Byte-for-byte the same precedence as server/utils/paystubCalc.js:
 *
 *   an amount ON THE ENTRY wins, and takes the entry's own type
 *   otherwise the JOB's type wins, with the job's amount
 *
 * The second line is why a custom entry needs its own amount: it has no job,
 * so there is nothing behind it to fall back to.
 *
 * @param {object} entry a row already passed through withJob()
 * @returns {{payType: string, payAmount: number}}
 */
export function resolveEntryPay(entry) {
  const ownAmount = parseFloat(entry.pay_amount || 0);
  const jobAmount = parseFloat(entry.job_pay_amount || 0);

  if (ownAmount > 0) {
    return { payType: entry.pay_type || entry.job_pay_type || 'hourly', payAmount: ownAmount };
  }
  return { payType: entry.job_pay_type || entry.pay_type || 'hourly', payAmount: jobAmount };
}

/** What one entry contributes, given the worker's fallback hourly rate. */
export function entryPay(entry, job, workerRate) {
  const { payType, payAmount } = resolveEntryPay(withJob(entry, job));
  const hours = parseFloat(entry.hours || 0) || 0;

  if (payType === 'fixed' && payAmount > 0) return payAmount;

  const rate = payAmount > 0 ? payAmount : (parseFloat(workerRate || 0) || 0);
  return hours * rate;
}

/**
 * The whole timesheet.
 *
 * CUSTOM ENTRIES ARE INCLUDED. They used to be skipped outright — the estimate
 * began `if (e.job_id === 'custom' || !e.job_id) return;` — so a $120 mileage
 * reimbursement showed as $0 of the total while payroll paid it. The figure was
 * not merely imprecise, it was answering a different question from the paystub,
 * which is the #202 shape again.
 *
 * @param {Array} entries form entries, job_id possibly the 'custom' sentinel
 * @param {Array} jobs    allJobs, for looking up job pay
 * @param {number|string|null} workerRate the worker's profile hourly rate
 * @returns {{total: number, hasRate: boolean}} hasRate is false when nothing
 *          could be priced at all, so the caller can stay silent rather than
 *          claim a confident $0.00.
 */
export function estimateTimesheetPay(entries, jobs, workerRate) {
  let total = 0;
  let hasRate = false;

  (entries || []).forEach((e) => {
    if (!e.job_id) return;                       // nothing chosen yet
    const isCustom = e.job_id === 'custom';
    const job = isCustom ? null : (jobs || []).find(j => j.id === parseInt(e.job_id, 10));

    // A row that cannot be priced must not silently count as zero.
    const { payType, payAmount } = resolveEntryPay(withJob(e, job));
    const fallback = parseFloat(workerRate || 0) || 0;
    const priceable = (payType === 'fixed' && payAmount > 0) || payAmount > 0 || fallback > 0;
    if (!priceable) return;

    total += entryPay(e, job, workerRate);
    hasRate = true;
  });

  return { total, hasRate };
}

/** A stored money column as a person reads it. Formatting; no arithmetic. */
function money(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

/**
 * What ONE line is priced at — 'Fixed $500.00', '$32.50/hr', or nothing.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * docs/PARITY_2026-09-08.md §5: "A fixed-price job's amount ... invisible on a
 * timesheet." hoursModel.sheetLines has always selected `j.pay_amount AS
 * job_pay_amount` and `j.pay_type AS job_pay_type` onto every line, and both
 * GET /api/timesheets/:id and GET /api/field/hours hand them over — to the
 * manager AND to the worker whose sheet it is. No screen drew either, so a
 * crew member working a fixed-price job saw hours and no price at all, and an
 * approver could not tell a $500 flat line from an hourly one.
 *
 * ── What it does NOT do, and this is the whole point ───────────────────────
 *
 * It resolves and it formats. It never multiplies and never sums.
 *
 * The precedence is resolveEntryPay above — mirrored against
 * server/utils/paystubCalc.js and run over the full matrix by
 * timesheetPay.test.js — so the label cannot name a source the paystub would
 * not use. What it must never become is a second implementation of the pay
 * ARITHMETIC: an `hours × rate` computed on a screen is #202's shape again, a
 * figure agreeing with neither the truth nor the paystub, with nothing on
 * screen ever contradicting anything.
 *
 * `null` when no amount is available anywhere, rather than '$0.00'. The last
 * fallback in paystubCalc is `users.hourly_rate`, and NEITHER timesheet
 * response carries it — so a figure here would be a claim about somebody's
 * pay assembled out of a field that was never sent. Silence is the honest
 * answer, and it is why the week's total is still not shown (see §5: the
 * estimate needs that rate, and computing it here would be the duplicated
 * arithmetic above).
 *
 * ── Per LINE, and what that no longer implies. Corrected 2026-09-11 ───────
 *
 * This used to read: "computeFigures adds a fixed entry's amount for every
 * entry, so a $500 job split across three days grosses $1500. The label
 * describes the row it sits on, which is what the server actually pays."
 *
 * The first sentence was the DEFECT and the second followed from it. A
 * job-priced fixed amount is now spent ONCE per paystub, so those three days
 * gross $500 and only the first line carries it.
 *
 * The label is still per line and still correct, because it answers "what is
 * this row PRICED at" — the column is headed Priced — and every one of those
 * three rows is on a $500 job. What it must not be read as is a column that
 * SUMS: three "Fixed $500.00" labels over a $500 week is a true statement
 * repeated, not an addition. The week's total still comes from the server and
 * still is not computed here.
 *
 * Saying WHICH line carries the amount would need the whole sheet, not one
 * row — the spend order is `paystubCalc.payLines`, and only it knows. That is
 * a change to a money screen and is deliberately not made from inside a
 * formatter.
 *
 * @param {object} line a timesheet line as the server sends it, already
 *                      carrying job_pay_type / job_pay_amount
 * @returns {string|null}
 */
export function linePriceLabel(line) {
  if (!line) return null;
  const { payType, payAmount } = resolveEntryPay(line);
  if (!(payAmount > 0)) return null;
  return payType === 'fixed' ? `Fixed ${money(payAmount)}` : `${money(payAmount)}/hr`;
}

/**
 * Rows that will not pay what whoever typed them expects.
 *
 * A custom entry set to 'fixed' with no amount falls through to the hourly
 * branch and pays hours x the worker's rate — which for a reimbursement with
 * no hours is $0, and for one with hours is a number nobody chose. Neither
 * errors. Returning the indexes lets the form refuse the save instead.
 */
export function unpricedFixedEntries(entries) {
  return (entries || []).reduce((out, e, i) => {
    const isFixed = (e.pay_type || '') === 'fixed';
    const amount = parseFloat(e.pay_amount || 0);
    if (isFixed && !(amount > 0)) out.push(i);
    return out;
  }, []);
}

/**
 * Is this line MONEY rather than hours?
 *
 * ── Why a screen needs to ask ──────────────────────────────────────────────
 *
 * The owner, 2026-09-10: "What if I want to adjust the timesheet to show
 * reinbursement, bonus, etc or other adjustment that has a set amount and not
 * hourly."
 *
 * server/utils/paystubCalc.js has always paid that row: computeFigures adds
 * `pay_amount` for a fixed entry and never multiplies it, and `hours`
 * contributes only to the hour total — so a $50 reimbursement stored with
 * `hours: 0` grosses $50 and adds no hours to the week. Nothing was missing
 * from the money. What was missing was the DRAWING: every place a line appears
 * prints its hours, so the $50 read "0:00" in the slot where its value
 * belongs, and "0.00" on the paystub's Hours column beside it.
 *
 * ── What it is, precisely ──────────────────────────────────────────────────
 *
 * A line that resolves to a flat amount AND logs no hours. Both halves matter:
 *
 *   a fixed-price job worked 8 hours   -> NOT an amount line. It pays $500
 *                                         flat (linePriceLabel says so) and
 *                                         those eight hours are the record an
 *                                         approver reconciles against the
 *                                         clock. Hiding them loses that.
 *   a reimbursement, hours 0           -> an amount line. The hours slot has
 *                                         nothing true to say.
 *
 * ── The trap it must not fall into ─────────────────────────────────────────
 *
 * resolveEntryPay keys off `pay_amount > 0`, so a line marked 'fixed' with NO
 * amount falls through computeFigures' fixed branch and is paid hours x the
 * worker's rate. Calling that an amount line would print a money slot on a row
 * the server pays by the hour — #202's shape exactly, a screen agreeing with
 * neither the truth nor the paystub. Hence the `> 0`, which is
 * resolveEntryPay's own test and not a second rule.
 *
 * It answers a question. It computes nothing: the figure a caller then prints
 * is linePriceLabel's, which formats a stored column.
 *
 * @param {object} line a timesheet line as the server sends it
 * @returns {boolean}
 */
export function isAmountLine(line) {
  if (!line) return false;
  const { payType, payAmount } = resolveEntryPay(line);
  if (payType !== 'fixed' || !(payAmount > 0)) return false;
  return !(parseFloat(line.hours || 0) > 0);
}

/**
 * The flat sum an amount line pays, formatted, with no word around it —
 * '$50.00' — or null for any line that is not one.
 *
 * linePriceLabel above says "Fixed $500.00" because it sits BESIDE an hours
 * figure and the reader has to be told which is which. An amount line shows
 * no hours, so the figure takes the slot the hours came out of, and what
 * belongs there is a number: '$50.00' set beside '8.50' on the row above,
 * not a sentence in a numeric column.
 *
 * Same resolver, same `> 0` guard, one fewer word. It formats a stored column
 * and multiplies nothing — the arithmetic stays in
 * server/utils/paystubCalc.js, which is the only thing that decides pay.
 *
 * @param {object} line a timesheet line as the server sends it
 * @returns {string|null}
 */
export function lineAmountLabel(line) {
  if (!isAmountLine(line)) return null;
  return money(resolveEntryPay(line).payAmount);
}

/**
 * WHICH rate a line is paid at, and where it came from.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Owner, 2026-09-28: "When admin creates timesheets, rate displays as
 * 0.00/hr. There rate should come from employee, job overrides rate for that
 * job and admin can override at the timesheet."
 *
 * That order is what server/utils/paystubCalc.payLines has always paid
 * ("Hourly: entry rate, then job rate, then the worker's profile rate"). What
 * the screen could not say was which of the three a line was on: linePriceLabel
 * above names the line's or the job's rate and falls silent on the employee's,
 * because no timesheet response carried it. The grid does now carry it, as
 * `grid.rate`, to whoever may read rates — so the third source can be named,
 * and so can the case the owner hit, where there is no source at all and the
 * line pays $0.00.
 *
 * ── The contract ───────────────────────────────────────────────────────────
 *
 *   { kind: 'fixed',  amount, source }            a flat amount ('sheet' | 'job')
 *   { kind: 'hourly', rate,   source }            source 'sheet' | 'job' | 'employee'
 *   { kind: 'hourly', rate: 0,    source: null }  NO rate anywhere: pays $0.00
 *   { kind: 'hourly', rate: null, source: null }  the employee's rate is
 *                                                  withheld from this reader, and
 *                                                  nothing on the line says more
 *
 * `workerRate` is `grid.rate`: a number for a reader who may see rates (0 when
 * the profile has none — the route reads NULL as 0), null when withheld.
 *
 * It picks a source; it multiplies nothing. timesheetPay.test.js runs it
 * against payLines over the full matrix, including the `priced` rule a fixed
 * job priced at $0 turns on, so it cannot name a rate the paystub would not
 * pay.
 *
 * @param {object} line a timesheet line as the server sends it
 * @param {number|null} workerRate grid.rate
 */
export function lineRate(line, workerRate) {
  const row = line || {};
  const { payType, payAmount } = resolveEntryPay(row);
  const own = parseFloat(row.pay_amount || 0) > 0;
  const named = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(parseFloat(v));
  // paystubCalc's `priced`: an own amount always is; a job's is when it names
  // a number, zero included.
  const priced = own || named(row.job_pay_amount);
  if (payType === 'fixed' && priced) return { kind: 'fixed', amount: payAmount, source: own ? 'sheet' : 'job' };
  if (payAmount > 0) return { kind: 'hourly', rate: payAmount, source: own ? 'sheet' : 'job' };
  if (workerRate === null || workerRate === undefined) return { kind: 'hourly', rate: null, source: null };
  const r = Number(workerRate) || 0;
  return r > 0 ? { kind: 'hourly', rate: r, source: 'employee' } : { kind: 'hourly', rate: 0, source: null };
}

/**
 * lineRate as a person reads it — '$25.00/hr', 'Fixed $500.00', 'No rate' —
 * and the source in words, separately, so a screen can set them apart.
 * `null` when there is nothing true to say (a withheld rate with no other).
 *
 * @param {object} line
 * @param {number|null} workerRate grid.rate
 * @param {string} [who] whose rate it is — "Maria's rate"; "Their rate" without
 * @returns {{text: string, from: string|null, none: boolean}|null}
 */
export function lineRateLabel(line, workerRate, who) {
  const r = lineRate(line, workerRate);
  if (r.kind === 'fixed') return { text: `Fixed ${money(r.amount)}`, from: r.source === 'sheet' ? 'set on this sheet' : 'job price', none: false };
  if (r.rate === null) return null;
  if (!(r.rate > 0)) return { text: 'No rate', from: null, none: true };
  const from = r.source === 'sheet' ? 'set on this sheet'
    : r.source === 'job' ? 'job rate'
      : `${who ? `${who}'s` : 'their'} rate`;
  return { text: `${money(r.rate)}/hr`, from, none: false };
}
